import { useTranslation } from "react-i18next";
import styles from "./StreamingDemo.module.css";

export type DemoPhase = "typing" | "thinking" | "streaming" | "done";

/** What the demo's meter shows: a bar of how much of something is used, and a line beneath it. */
export interface DemoMeter {
  /** The bar's accessible name. */
  label: string;
  /** How full the bar is, from 0 to 1. */
  level: number;
  /** The line beneath the bar. */
  status: string;
}

/**
 * The pretend model's window and its meter, drawn from whatever it is told: the everyday streaming demo and the easter egg both use it,
 * so they look like the same window.
 *
 * @param props.windowTitle - The title bar's text.
 * @param props.phase - Where the exchange is, which sets the title bar's status and its dot.
 * @param props.prompt - The prompt typed so far.
 * @param props.answer - The answer's tokens revealed so far; shown once the phase is streaming or done.
 * @param props.tokens - The count the meter reads out.
 * @param props.cost - The cost the meter reads out, already formatted.
 * @param props.rate - The example rate the meter reads out, already formatted.
 * @param props.meter - The bar and its line.
 * @param props.awakened - True for the easter egg, which lights the window.
 */
export function DemoPanel({
  windowTitle,
  phase,
  prompt,
  answer,
  tokens,
  cost,
  rate,
  meter,
  awakened = false,
}: {
  windowTitle: string;
  phase: DemoPhase;
  prompt: string;
  answer: readonly string[];
  tokens: string;
  cost: string;
  rate: string;
  meter: DemoMeter;
  awakened?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={styles.demo} data-awakened={awakened}>
      <div className={styles.window}>
        <div className={styles.titleBar}>
          <span className={styles.lights} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className={styles.windowTitle}>{windowTitle}</span>
          <span className={styles.live} data-phase={phase}>
            {t(`signedOut.demo.phase.${phase}`)}
          </span>
        </div>
        <div className={styles.transcript}>
          <p className={styles.prompt}>
            <span className={styles.promptMark} aria-hidden="true">
              ›
            </span>
            {prompt}
            {phase === "typing" && <span className={styles.caret} aria-hidden="true" />}
          </p>
          {phase === "thinking" && (
            <p className={styles.thinking}>
              <span className="visually-hidden">{t("signedOut.demo.phase.thinking")}</span>
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </p>
          )}
          {(phase === "streaming" || phase === "done") && (
            <p className={styles.answer}>
              {answer.map((piece, position) => (
                <span key={position} className={styles.token}>
                  {piece}
                </span>
              ))}
              {phase === "streaming" && <span className={styles.caret} aria-hidden="true" />}
            </p>
          )}
        </div>
      </div>

      <div className={styles.meter}>
        <dl className={styles.readings}>
          <div>
            <dt>{t("signedOut.demo.tokens")}</dt>
            <dd>{tokens}</dd>
          </div>
          <div>
            <dt>{t("signedOut.demo.cost")}</dt>
            <dd>{cost}</dd>
          </div>
          <div>
            <dt>{t("signedOut.demo.rate")}</dt>
            <dd>{rate}</dd>
          </div>
        </dl>
        <div
          className={styles.allowance}
          role="meter"
          aria-label={meter.label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(meter.level * 100)}
        >
          <span
            className={styles.allowanceFill}
            data-state={meter.level >= 1 ? "reached" : meter.level >= 0.8 ? "near" : "ok"}
            style={{ width: `${(meter.level * 100).toFixed(1)}%` }}
          />
        </div>
        <p className={styles.status}>{meter.status}</p>
      </div>
    </div>
  );
}
